

import { Guest } from "@/types/Guest";
import { Card } from "../Card/Card";
import { Spacing } from "../Spacing/Spacing";
import { GuestInfos } from "./components/GuestInfos";
import { GuestDataExportButton } from "./data-export/GuestDataExportButton";
import { GuestNotes } from "./guest-notes/GuestNotes";
import { GuestActions } from "./guest-actions/GuestActions";
import { useMetaTable } from "@/providers/MetaTableProvider";
import {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLazyOpen } from "@/hooks/useLazyOpen";
import { useInvalidFields } from "@/hooks/useInvalidFields";
import { useGuestWorkflowPreview } from "@/hooks/useGuestWorkflowPreview";
import { WorkflowOverwriteProvider } from "../Inputs/WorkflowFieldBadge";
import { EmailTrigger } from "@/types/enums/EmailTrigger";
import { EventContext } from "@/providers/EventProvider";
import { guestToCreationFields } from "@/lib/utils/guestCreationFields";
import { hasGuestChanges } from "@/lib/utils/parser";
import { PDeleteConfirmation } from "../PDeleteConfirmation/PDeleteConfirmation";
import { CreationFieldsRef } from "../PCreateGuest/PCreateGuest";
import { CreateGuestBody } from "../PCreateGuest/CreateGuestBody";
import {
  IconArrowLeft,
  IconLayoutGrid,
  IconLoader2,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useNavigate } from "react-router-dom";
import { useGuestCompanions } from "@/providers/GuestCompanions";
import { COMPANION_SCOPE } from "@/types/enums/CompanionScope";
import { useGuests } from "@/providers/GuestsProvider";
import { useLiveGuestPatches } from "@/hooks/useGuestPatchSync";
import { AcceptanceState } from "@/types/enums/AcceptanceState";
import { useRole } from "@/providers/RoleProvider";
import { GuestDetailLayoutPicker } from "./components/GuestDetailLayoutPicker";
import { useResolvedGuestDetailLayout } from "./layout/useResolvedGuestDetailLayout";
import {
  RequestDetailSurface,
  requestSurfaceOfGuestType,
  useCreateLayout,
} from "./layout/createLayoutSurface";
import {
  LayoutTarget,
  urlOfLayoutTarget,
} from "../builders/guest-layout/layoutTarget";
import { buildPlaceholderCompanions } from "@/lib/guests/placeholderCompanions";
import { useEventGuestQuota } from "@/lib/query/hooks/useEventGuestQuota";
import { GDL_STANDARD } from "@/lib/storage/guestDetailLayoutPref";
import { EDL_STANDARD } from "@/lib/storage/entityDetailLayoutPref";

/** "New custom field" — same header affordance the CREATE dialog carries
 *  (CreateGuestBody). Layout-independent, since a custom field can sit in any
 *  group. Lazy: the editor is a big chunk nobody pays for until clicked. */
// B2 (FE-6): the dialog + the "hidden in N 'All except' roles" toast host.
const loadAdditionalField = () => import("../PAdditionalField/PAdditionalFieldWithRoleHint");
const PAdditionalField = lazy(() =>
  loadAdditionalField().then((m) => ({ default: m.PAdditionalFieldWithRoleHint })),
);

type GuestDetailBodyProps = {
  onCloseAction: () => void;
  guest: Guest;
  notes: boolean;
  avoidStateUpdate?: boolean;
  parentGuestName?: string;
};

export const GuestDetailBody: React.FC<GuestDetailBodyProps> = ({
  onCloseAction,
  guest,
  notes,
  avoidStateUpdate = false,
  parentGuestName,
}) => {
  const t = useTranslations("guest-details");
  // Tooltip for the "+" — reuses the create dialog's label, so both headers
  // name the same affordance identically.
  const tCreate = useTranslations("popup-create-guest");
  // The arrival-notice block's settings links leave the page (audit r1 A5-6):
  // the generic "leave this page?" copy, not the layout builder's.
  const tLeave = useTranslations("leaveSide");
  const tUnsaved = useTranslations("unsavedChanges");
  const { legacyRoleFloor, role, isArchived, isPlanLocked } = useRole();
  // The list's flag: what the guest was when the popup opened. It keeps
  // deciding the popup's SHAPE — the pending-request decision view, the
  // header's Accept / Decline — for as long as it is open.
  const { archived } = useMetaTable();
  // PB-FB-L22-ARCHIVE: an Archive / Unarchive in the popup's header
  // (`onArchivedChange`) is newer — but it moves the layout's portal-link cell
  // and the header's own Archive / Unarchive toggle ONLY (the header unmounts
  // while a companion or the quick-add is open here, and mounted again on the
  // list's flag it offered "Archive" for a guest just archived). Fed into
  // `isPendingRequest` it turned an archived pending guest's full popup, on
  // Unarchive, into the stripped decision view (the notes unmounted with a
  // typed draft, the header's actions gone).
  // Keyed on the guest, so a companion opened inside keeps its own.
  const [archivedHere, setArchivedHere] = useState<{ guestId: string | undefined; value: boolean } | null>(null);
  const latestArchived =
    archivedHere !== null && archivedHere.guestId === guest.guestId ? archivedHere.value : archived;
  const handleArchivedChange = useCallback(
    (value: boolean) => setArchivedHere({ guestId: guest.guestId, value }),
    [guest.guestId],
  );
  const [openingId, setOpeningId] = useState<string | Guest>("");
  const navigate = useNavigate();
  const { addGuest, addGuests, updateGuest, guests, eventId: guestsEventId } =
    useGuests();
  const { fetchCompanions } = useGuestCompanions();
  const [creationFields, setCreationFields] = useState<CreationFieldsRef>(() =>
    guestToCreationFields(guest),
  );

  const creationFieldsDb = useRef<CreationFieldsRef>(
    guestToCreationFields(guest),
  );

  // Inline "new custom field" editor (header "+"), like the create dialog.
  const [isFieldPopupOpen, setFieldPopupOpen] = useState(false);
  // Leaving for the layout builder is a full page change, and this popup can
  // hold unsaved edits (Save/Undo). Ask first — same guard, same copy as
  // CreateGuestBody's identical jump. The arrival-notice block's links to the
  // notifications settings page take the same guard (audit r1 A5-6), with the
  // generic "leave this page" copy.
  const [pendingNav, setPendingNav] = useState<{ url: string; kind: "layout" | "leave" } | null>(null);
  const { opening: openingField, open: openFieldEditor } =
    useLazyOpen(loadAdditionalField);

  // Bound-aware inline validation messages from a failed save, keyed by
  // additional-field NAME. GuestActions populates it from the backend
  // violations; GuestInfos → AdditionalFields renders each under its input and
  // clears one as the admin edits that field.
  const [additionalFieldErrors, setAdditionalFieldErrors] = useState<
    Record<string, string>
  >({});
  const clearFieldError = useCallback((fieldName: string) => {
    setAdditionalFieldErrors((prev) => {
      if (!(fieldName in prev)) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);
  // Boxes whose text `creationFields` does NOT hold (an unstorable email —
  // the cell keeps the stored address in the form and shows the typo in red).
  // GuestActions' Save refuses while one exists; without that it would write
  // the stored address back and look like success under a red box.
  const invalidFields = useInvalidFields();

  // IMAGE additional fields persist at upload-confirm time (presign→confirm
  // writes guest.additionalFields server-side) — the popup's Save must NOT
  // light up dirty from that echo. Sync the confirmed value into BOTH the
  // live form state and the pristine baseline; the "" clear path stays on the
  // normal onChangeValue → dirty → Save flow (a clear IS a pending change).
  const handleAdditionalFieldPersisted = useCallback(
    (fieldName: string, value: unknown) => {
      creationFieldsDb.current = {
        ...creationFieldsDb.current,
        additionalFields: {
          ...(creationFieldsDb.current.additionalFields ?? {}),
          [fieldName]: value,
        },
      };
      setCreationFields((prev) => ({
        ...prev,
        additionalFields: {
          ...(prev.additionalFields ?? {}),
          [fieldName]: value,
        },
      }));
    },
    [],
  );

  /**
   * "Only the number is known" — mint `count` nameless companions of THIS
   * guest in ONE request (see PlaceholderCompanions).
   *
   * Errors travel up deliberately: the quick-add row renders them where the
   * operator is looking, and the capacity sentinel has to reach it too so the
   * form closes for the popup instead of printing the sentinel as a message.
   */
  const createPlaceholderCompanions = useCallback(
    async (count: number) => {
      const hostId = guest.guestId;
      if (!hostId) return;
      // The created rows reach the sidebar through GuestCompanions' own
      // `guestsCreated` listener, NOT from here. That is what makes the
      // capacity lane work: on a full event this call throws the
      // CAPACITY_DECISION_PENDING sentinel and the rows are created later, by
      // the popup's confirm — a call site that has no idea this list exists.
      // EVERY companion row, asked for here rather than read out of the shared
      // map: the numbering continues after the highest existing "TBD n", and a
      // narrower list (the panel's default hides archived and declined rows)
      // makes "+2" mint a second "TBD 1" for a party that already has one.
      const existing = await fetchCompanions(hostId, false, COMPANION_SCOPE.ALL);
      await addGuests(buildPlaceholderCompanions(count, hostId, existing));
    },
    [addGuests, fetchCompanions, guest.guestId],
  );

  // Phase B: live per-field overwrite preview, recomputed as the admin edits.
  // Feeds the inline badges (via context) on every field an enabled automation
  // (UPDATE_GUEST on GUEST_UPDATED) will change. Same predictor the save-time
  // dialog uses, just surfaced inline instead of at confirm.
  // guest.eventId isn't always populated on list-loaded guests; fall back to
  // the event-scoped surface so the preview/badges still resolve. Raw context
  // (not useEventContext) so it degrades to undefined off an event page.
  const eventCtx = useContext(EventContext);
  const previewEventId =
    guest.eventId || guestsEventId || eventCtx?.event?.eventId;

  // Contingent of the company that OWNS this event (cached, 30s stale — the
  // guest list has usually fetched it already). Only the companion quick-add
  // reads it: it is the one control here that can spend up to 50 units at once.
  const guestQuota = useEventGuestQuota(previewEventId ?? null);

  // The companion sub-pane below renders a SECOND GuestDetailBody from an
  // object the companions list captured — and unlike the popup around us it has
  // no refetch and no poll, so a save made in there used to be invisible to its
  // own read-only consumers FOREVER (the send dialog's consent warning being
  // the one that got reported). Follow the confirmed patch instead.
  const openedCompanion = typeof openingId === "string" ? null : openingId;
  const liveOpenedCompanion = useLiveGuestPatches(openedCompanion, previewEventId);
  // PENDING request (opened from the requests page, or a pending guest from
  // anywhere): the popup is a decision surface, not a management one — strip
  // it to the data + Save/Undo + Accept/Decline. No appointments/notes/
  // companions sidebar, no check-in/QR/archive/delete chrome, no layout
  // PICKER and no "+" (the layout-builder entry stays — see the header).
  // After the decision the guest refetch flips acceptanceState and
  // the popup grows into the full view on its own.
  const isPendingRequest =
    guest.acceptanceState === AcceptanceState.PENDING && !archived;

  // A pending request is decided on ITS OWN lane's layout
  // (`detailLayouts.registrantDetail` / `.companionDetail`), never the create
  // form's and never silently the guest-detail one: the header's builder button
  // has to land where a save changes the popup the admin is looking at. Until
  // that lane is configured it FALLS BACK to the guest-detail layout, so this
  // costs an untouched event exactly nothing (see GuestDetailLayoutView).
  const requestSurface: RequestDetailSurface | undefined = isPendingRequest
    ? requestSurfaceOfGuestType(guest.guestType)
    : undefined;

  // Which layout the popup currently shows — the builder-entry button opens
  // the builder on exactly this layout. BOTH hooks run on every render (hook
  // order is not negotiable), but each is handed a null eventId in the other's
  // mode, so only one of them ever issues a request. Same recipe as
  // GuestDetailLayoutView, which RESOLVES the layout these two only name.
  const { selectedId: detailLayoutId } = useResolvedGuestDetailLayout(
    requestSurface ? null : previewEventId,
  );
  const { selectedId: createLayoutId } = useCreateLayout(
    requestSurface ? previewEventId : null,
    requestSurface ?? "guestCreate",
  );
  // Standard carries NO id in the url — the builder route reads a bare path as
  // "this lane's Standard layout", and passing the sentinel would look up a
  // named layout that does not exist.
  const layoutTarget: LayoutTarget = requestSurface
    ? {
        kind: "requestDetail",
        surface: requestSurface,
        layoutId:
          createLayoutId && createLayoutId !== EDL_STANDARD
            ? createLayoutId
            : undefined,
      }
    : {
        kind: "detail",
        layoutId: detailLayoutId !== GDL_STANDARD ? detailLayoutId : undefined,
      };
  const layoutBuilderUrl = previewEventId
    ? urlOfLayoutTarget(previewEventId, layoutTarget)
    : null;
  // A route change unmounts this popup (it lives in the guest list page and
  // there is no route blocker). Same predicate as the Save button's dirty
  // flag, so the prompt appears exactly when Save is armed.
  const requestNavigate = useCallback(
    (url: string, kind: "layout" | "leave") => {
      if (hasGuestChanges(creationFields, creationFieldsDb.current)) {
        setPendingNav({ url, kind });
        return;
      }
      navigate(url);
    },
    [creationFields, navigate],
  );
  const { getOverwrites } = useGuestWorkflowPreview(previewEventId);
  const overwriteMap = useMemo(() => {
    const preview = getOverwrites(
      creationFields as unknown as Record<string, unknown>,
      creationFieldsDb.current as unknown as Record<string, unknown>,
      EmailTrigger.GUEST_UPDATED,
    );
    return new Map(preview.entries.map((e) => [e.field, e]));
  }, [getOverwrites, creationFields]);

  const handleSaveCredential = useCallback(
    async (fieldName: string, value: string) => {
      // Frontend validation: check for duplicates among loaded guests
      if (value) {
        const isDuplicate = (g: Guest) =>
          g.guestId !== guest.guestId &&
          g[fieldName as keyof Guest] === value;
        const duplicate =
          guests.find(isDuplicate) ||
          guests.flatMap((g) => g.companions || []).find(isDuplicate);
        if (duplicate) {
          throw new Error(
            fieldName === "username"
              ? "usernameAlreadyTaken"
              : "codeAlreadyTaken",
          );
        }
      }

      const partialGuest = { guestId: guest.guestId, [fieldName]: value } as Guest;
      await updateGuest(
        partialGuest,
        avoidStateUpdate,
        creationFields.mainGuest,
        creationFields.mainGuest,
      );
      creationFieldsDb.current = {
        ...creationFieldsDb.current,
        [fieldName]: value,
      };
    },
    [guest.guestId, guests, avoidStateUpdate, creationFields.mainGuest, updateGuest],
  );

  // Inline nested view (no Card wrapper) — used when opening a companion from within the sidebar
  if (!notes) {
    const backLabel = parentGuestName || t("guestDetails");
    return (
      <div className="flex grow flex-col min-h-0 overflow-hidden surface-primary rounded-2xl border outline-quaternary">
        <button
          onClick={onCloseAction}
          className="flex items-center gap-2 px-4 sm:px-6 pt-4 sm:pt-5 pb-2 font-secondary body-2-medium cursor-pointer hover:font-primary transition-colors"
        >
          <IconArrowLeft size={16} />
          {backLabel}
        </button>
        <div className="flex flex-col min-h-0 overflow-y-auto px-4 sm:px-6 pb-4 sm:pb-6">
          {legacyRoleFloor !== "VIEWER" && (
            <GuestActions
              avoidUpdate={avoidStateUpdate}
              guest={guest}
              acceptanceState={guest.acceptanceState}
              creationFields={creationFields}
              creationFieldsDb={creationFieldsDb}
              setCreationFieldsAction={setCreationFields}
              archived={archived}
              headerArchived={latestArchived}
              onCloseAction={onCloseAction}
              hasInvalidFields={invalidFields.has}
              requestMode={isPendingRequest}
              onArchivedChange={handleArchivedChange}
            />
          )}
          <WorkflowOverwriteProvider value={overwriteMap}>
            <GuestInfos
              surface={requestSurface}
              eventId={previewEventId}
              creationFields={creationFields}
              setCreationFieldsAction={setCreationFields}
              notes={false}
              currentGuestId={guest.guestId}
              guest={guest}
              onSaveCredential={handleSaveCredential}
              additionalFieldErrors={additionalFieldErrors}
              onClearFieldError={clearFieldError}
              onAdditionalFieldPersisted={handleAdditionalFieldPersisted}
              onFieldInvalid={invalidFields.onFieldInvalid}
              archived={latestArchived}
            />
          </WorkflowOverwriteProvider>
        </div>
      </div>
    );
  }

  return (
    <Card
      padding="p-4 sm:p-6"
      width={
        isPendingRequest
          ? "w-full sm:w-[92vw] sm:max-w-[min(64rem,calc(100vw-var(--ai-rail-push)-4rem))]"
          : "w-full sm:w-[92vw] sm:max-w-[min(72rem,calc(100vw-var(--ai-rail-push)-4rem))]"
      }
      rounded="rounded-t-2xl sm:rounded-2xl"
      marginOuter="m-0 sm:m-6"
      surfaceColor="surface-secondary"
      className="overflow-hidden"
      justify="flex flex-col"
      // dvh, not vh: on mobile Safari `vh` is the LARGE viewport (toolbars
      // hidden), so 90vh overflowed the visible height and — with the sheet
      // docked to the bottom — the overflow escaped off the TOP of the screen.
      // max-h in the same unit could not clamp it. h-dvh is the house unit
      // (195 uses); PFixedGuestDetails is the in-family precedent.
      height="max-h-[95dvh] sm:max-h-200 h-[90dvh]"
      shadow="shadow-2xl"
    >
      {/* flex-wrap + min-w-0: the action cluster's intrinsic min-width is its
          widest button, so without these the icon cluster (which is shrink-0)
          pushed the close button out of the Card's overflow-hidden box. */}
      <div className="flex flex-row flex-wrap items-start gap-2">
        {!openingId && legacyRoleFloor !== "VIEWER" && (
          <GuestActions
            avoidUpdate={avoidStateUpdate}
            guest={guest}
            acceptanceState={guest.acceptanceState}
            creationFields={creationFields}
            creationFieldsDb={creationFieldsDb}
            setCreationFieldsAction={setCreationFields}
            archived={archived}
            headerArchived={latestArchived}
            onCloseAction={onCloseAction}
            setAdditionalFieldErrorsAction={setAdditionalFieldErrors}
            hasInvalidFields={invalidFields.has}
            requestMode={isPendingRequest}
            onArchivedChange={handleArchivedChange}
          />
        )}
        <div className="ml-auto flex items-center gap-1 shrink-0 order-first sm:order-none">
          {/* Per-user layout switcher — cosmetic, so no role gate (VIEWER
              included). Hides itself when the event has no named layouts. */}
          {!openingId && !isPendingRequest && (
            <GuestDetailLayoutPicker eventId={previewEventId} />
          )}
          {/* New custom field, same as the create dialog's header "+". Hidden
              in create mode (CreateGuestBody brings its own) and in the
              stripped-down pending-request view; a field definition is event
              config, so archived / plan-locked events don't offer it. */}
          {!openingId &&
            !isPendingRequest &&
            legacyRoleFloor !== "VIEWER" &&
            !isArchived &&
            !isPlanLocked && (
              <button
                type="button"
                data-testid="guest-details-add-field"
                title={tCreate("additionalFields")}
                aria-label={tCreate("additionalFields")}
                disabled={openingField}
                onClick={() => openFieldEditor(() => setFieldPopupOpen(true))}
                className="cursor-pointer p-1 font-tertiary hover:font-primary transition-colors disabled:opacity-50"
              >
                {openingField ? (
                  <IconLoader2 size={18} className="animate-spin" />
                ) : (
                  <IconPlus size={18} />
                )}
              </button>
            )}
          {/* Layout builder entry — opens the dedicated full-page builder ON
              THE LAYOUT CURRENTLY SHOWN (editing "Sales" while looking at
              "Sales" — landing on Standard instead would save invisibly).
              Team-shared config, so gated to real EDITOR+ (the narrow
              endpoint 403s CUSTOM event roles) on a writable (not archived /
              plan-locked) event.
              Kept in the PENDING-request view too (unlike the picker and the
              "+"), where it opens that request's OWN lane
              (`detailLayouts.registrantDetail` / `.companionDetail`) — the
              request popup is exactly where an admin notices a field missing
              from the decision surface, and a jump to "Gast bearbeiten" would
              save into a document this popup does not read. */}
          {!openingId &&
            previewEventId &&
            legacyRoleFloor !== "VIEWER" &&
            role !== "CUSTOM" &&
            !isArchived &&
            !isPlanLocked && (
              <button
                type="button"
                data-testid="guest-layout-builder-open"
                title={t("layoutEdit")}
                aria-label={t("layoutEdit")}
                onClick={() => {
                  if (!layoutBuilderUrl) return;
                  requestNavigate(layoutBuilderUrl, "layout");
                }}
                className="cursor-pointer p-1 font-tertiary hover:font-primary transition-colors"
              >
                <IconLayoutGrid size={18} />
              </button>
            )}
          {/* Art. 15 export (Datenauskunft) — the one cockpit door to the
              stored consent rows since the Consent block left this popup
              (Nikita 29.10.2026). Kept for archived guests and pending
              requests too: the right of access covers every stored guest.
              The button gates itself (company role in the event's company). */}
          {!openingId && (
            <GuestDataExportButton guest={guest} eventId={previewEventId} />
          )}
          <button
            type="button"
            className="cursor-pointer p-1"
            onClick={onCloseAction}
            aria-label={t("close")}
            title={t("close")}
            data-testid="guest-detail-close"
          >
            <IconX size={18} className="font-secondary" aria-hidden />
          </button>
        </div>
      </div>
      {/* Phones scroll the WHOLE body as one column — two nested scroll areas
          in a bottom sheet leave each pane a few rows tall. Same doctrine as
          ItemDetailLayout in the program manager. */}
      {/* `overscroll-contain`: this panel is a layer over the guest list, and
          the mail dialog is a layer over it. Without it, reaching the end of
          one scroller hands the rest of the gesture to whatever is underneath —
          on iOS the list behind starts moving and the finger stays with it, so
          the layer the user is actually looking at stops responding until they
          lift off. Inert from md up, where this box is not a scroller. */}
      <div className="flex grow flex-col md:flex-row min-h-0 overflow-y-auto overscroll-contain md:overflow-hidden gap-3">
        {openingId === "create" ? (
          <CreateGuestBody
            onCreation={async (creationFields) => {
              // The new row reaches the companion sidebar through
              // GuestCompanions' `guestsCreated` listener. This used to append
              // it by hand here, which meant a companion created after the
              // capacity popup took over — a lane that never returns to this
              // callback — was invisible in the sidebar for the rest of the
              // page visit. (The first of the two branches it used to have was
              // dead anyway: it compared the new companion's `mainGuest` with
              // its OWN id.)
              await addGuest(creationFields as unknown as Guest);
            }}
            mainGuest={false}
            companion={false}
            mainGuestId={guest.guestId}
            // The quick-add is the only lane in the app that can spend 50
            // contingent units on one click; the guest list gates its own
            // create buttons on exactly this value.
            freeContingent={guestQuota.data?.freeContingent ?? undefined}
            onCreatePlaceholdersAction={createPlaceholderCompanions}
            onClose={() => setOpeningId("")}
          />
        ) : openingId && typeof openingId !== "string" ? (
          <GuestDetailBody
            notes={false}
            onCloseAction={() => setOpeningId("")}
            guest={(liveOpenedCompanion ?? openingId) as Guest}
            parentGuestName={[guest.firstName, guest.lastName].filter(Boolean).join(" ")}
          />
        ) : (
          <WorkflowOverwriteProvider value={overwriteMap}>
            <GuestInfos
              surface={requestSurface}
              eventId={previewEventId}
              creationFields={creationFields}
              setCreationFieldsAction={setCreationFields}
              notes={true}
              currentGuestId={guest.guestId}
              guest={guest}
              onSaveCredential={handleSaveCredential}
              additionalFieldErrors={additionalFieldErrors}
              onClearFieldError={clearFieldError}
              onAdditionalFieldPersisted={handleAdditionalFieldPersisted}
              onFieldInvalid={invalidFields.onFieldInvalid}
              archived={latestArchived}
              // Nothing under the field layout any more: the arrival-notice
              // recipients are the layout cell "Ankunft melden an" (plan
              // arrival-automation Rev. 4 §1.8, OWNER/ADMIN only, see
              // ArrivalNoticeLayoutCell), and the consent evidence (PB-RCS-1)
              // is no longer shown here (Nikita 29.10.): the opt-in fields
              // carry the state, the manual mail send dialogs warn on "none"
              // and marketing skips "no", SMS goes only to "yes", and the
              // stored rows stay in the Art. 15 export.
            />
          </WorkflowOverwriteProvider>
        )}
        {!isPendingRequest && (
          <GuestNotes
            openingId={openingId}
            setOpeningId={setOpeningId}
            companion={guest.guestType === "companion"}
            guestId={guest.guestId!}
            firstName={guest.firstName}
            lastName={guest.lastName}
            eventId={previewEventId}
            hostArchived={!!guest.archived}
          />
        )}
      </div>
      {isFieldPopupOpen && (
        <Suspense fallback={null}>
          <PAdditionalField setIsPopupOpen={setFieldPopupOpen} eventId={previewEventId} />
        </Suspense>
      )}
      {/* Unsaved-changes prompt for a jump off the page. The layout builder's
          uses the same three strings as the create dialog's identical jump —
          one affordance, one copy; the notifications settings links say
          "leave this page". */}
      {pendingNav && (
        <PDeleteConfirmation
          title={t("createLayoutDiscardTitle")}
          message={pendingNav.kind === "layout" ? t("createLayoutDiscardBody") : tLeave("message")}
          primaryButtonText={pendingNav.kind === "layout" ? t("createLayoutDiscardConfirm") : tUnsaved("discard")}
          isLoading={false}
          onCloseAction={() => setPendingNav(null)}
          onConfirmAction={async () => {
            const { url } = pendingNav;
            setPendingNav(null);
            navigate(url);
          }}
        />
      )}
    </Card>
  );
};
